import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useVendorAuth } from "@/hooks/useVendorAuth";
import { useDriverTheme } from "@/hooks/useDriverTheme";
import { Input } from "@/components/ui/input";
import { Phone, AlertCircle, Loader2 } from "lucide-react";
import VendorIcon from "@/assets/Vendor_Icon.png";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

export default function VendorLogin() {
  const navigate = useNavigate();
  const { tokens, isDark } = useDriverTheme();
  const vendorAuth = useVendorAuth();

  const [step, setStep] = useState<"phone" | "otp">("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [userName, setUserName] = useState("");
  
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  useEffect(() => {
    if (!vendorAuth.loading && vendorAuth.isAuthenticated) {
      navigate("/vendor/dashboard", { replace: true });
    }
  }, [vendorAuth.isAuthenticated, vendorAuth.loading, navigate]);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const id = setInterval(() => setResendTimer((t) => t - 1), 1000);
    return () => clearInterval(id);
  }, [resendTimer]);

  const handleSendOtp = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError(null);
    setIsSubmitting(true);
    const cleanPhone = phone.trim();
    if (!cleanPhone || cleanPhone.length !== 10) {
      setError("Enter a valid 10-digit phone number");
      setIsSubmitting(false);
      return;
    }
    const result = await vendorAuth.sendOtp(cleanPhone);
    setIsSubmitting(false);
    if (!result.success) {
      setError(result.error || "Failed to send OTP");
      return;
    }
    setUserName(result.vendorName || "");
    setStep("otp");
    setResendTimer(30);
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length !== 4) return;
    setError(null);
    setIsSubmitting(true);
    const result = await vendorAuth.verifyOtp(phone.trim(), otp);
    setIsSubmitting(false);
    if (!result.success) {
      setError(result.error || "Invalid OTP");
    }
  };

  const handleResendOtp = () => {
    setOtp("");
    setError(null);
    handleSendOtp();
  };

  if (vendorAuth.loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center" style={{ background: tokens.bgPage }}>
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div
      className="flex min-h-dvh flex-col items-center justify-center p-4"
      style={{
        background: tokens.bgPage,
        paddingTop: "env(safe-area-inset-top, 0px)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <p className="text-sm" style={{ color: tokens.textSecondary }}>
            Welcome to
          </p>
          <h1 className="text-4xl font-bold tracking-tight" style={{ color: tokens.textPrimary }}>
            Safe Track
          </h1>
          <p className="text-2xl font-bold text-orange-500">Vendor</p>
          <div className="mx-auto mt-4">
            <img src={VendorIcon} alt="Vendor" className="h-16 w-16 rounded-2xl object-contain block mx-auto" />
          </div>
        </div>

        <div
          className="rounded-2xl border-0 p-6"
          style={{
            background: tokens.bgCard,
            boxShadow: isDark ? "0 8px 32px rgba(0,0,0,0.4)" : "0 8px 32px rgba(0,0,0,0.08)",
          }}
        >
          <div className="space-y-5">
            <h2 className="text-center text-lg font-semibold" style={{ color: tokens.textPrimary }}>
              Vendor Login
            </h2>

            {error && (
              <div className="flex items-center gap-2 rounded-lg bg-red-500/10 p-3 text-sm text-red-400">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}


            {step === "phone" ? (
              <form onSubmit={handleSendOtp} className="space-y-4">
                <div className="relative">
                  <Phone
                    className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
                    style={{ color: tokens.textSecondary }}
                  />
                  <Input
                    type="tel"
                    inputMode="numeric"
                    placeholder="Enter phone number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    maxLength={10}
                    className="pl-10"
                    style={{
                      background: isDark ? "rgba(255,255,255,0.06)" : "#F9FAFB",
                      borderColor: isDark ? "rgba(255,255,255,0.1)" : "#E5E7EB",
                      color: tokens.textPrimary,
                    }}
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full rounded-xl py-3 text-sm font-semibold text-white transition-all disabled:opacity-50"
                  style={{ background: "#F97316" }}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" /> Sending...
                    </span>
                  ) : (
                    "Send OTP"
                  )}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOtp} className="space-y-4">
                <p className="text-center text-sm" style={{ color: tokens.textSecondary }}>
                  OTP sent to{" "}
                  <span className="font-medium" style={{ color: tokens.textPrimary }}>
                    {phone}
                  </span>
                  {userName ? ` — Hi, ${userName}` : ""}
                </p>
                <div className="flex justify-center">
                  <InputOTP maxLength={4} value={otp} onChange={setOtp}>
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <div className="text-center text-sm">
                  {resendTimer > 0 ? (
                    <span style={{ color: tokens.textSecondary }}>Resend OTP in {resendTimer}s</span>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      className="font-medium text-orange-500 hover:underline"
                    >
                      Resend OTP
                    </button>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting || otp.length !== 4}
                  className="w-full rounded-xl py-3 text-sm font-semibold text-white transition-all disabled:opacity-50"
                  style={{ background: "#F97316" }}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" /> Verifying...
                    </span>
                  ) : (
                    "Verify & Login"
                  )}
                </button>
                <button
                  type="button"
                  className="w-full rounded-xl py-2.5 text-sm font-medium transition-colors"
                  style={{ color: tokens.textSecondary }}
                  onClick={() => {
                    setStep("phone");
                    setOtp("");
                    setError(null);
                    setResendTimer(0);
                  }}
                >
                  Change Number
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="mt-5 text-center">
          <Link
            to="/vendor/register"
            className="inline-block rounded-full px-6 py-2.5 text-sm font-medium text-orange-500 transition-colors"
            style={{ border: "1px solid rgba(249,115,22,0.3)" }}
          >
            New Vendor Registration
          </Link>
          <p className="mt-4 text-xs" style={{ color: tokens.textPrimary }}>
            By continuing, I agree to the{" "}
            <Link to="/vendor/terms" className="text-orange-500 font-medium">
              Terms and Conditions
            </Link>{" "}
            and the{" "}
            <Link to="/vendor/privacy" className="text-orange-500 font-medium">
              Privacy Policy
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
